<script setup lang="ts">
import {
  BankOutlined,
  BellOutlined,
  BookOutlined,
  CalendarOutlined,
  DashboardOutlined,
  DesktopOutlined,
  DollarOutlined,
  GlobalOutlined,
  HomeOutlined,
  InboxOutlined,
  MailOutlined,
  MessageOutlined,
  NotificationOutlined,
  SettingOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons-vue'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'

// ant-design-vue uses a Key type of string | number for menu keys
type Key = string | number

interface MenuItem {
  key: string
  label: string
  icon?: any
  children?: MenuItem[]
}

const props = defineProps<{ collapsed: boolean; openKeys: Key[] }>()
const emit = defineEmits<{ 'update:openKeys': [keys: Key[]]; menuClick: [key: string] }>()

const themeStore = useThemeStore()
const authStore = useAuthStore()
const menuTheme = computed(() => (themeStore.isDark ? 'dark' : 'light'))

const route = useRoute()
const router = useRouter()

const menuItems = computed(() => {
  const items: MenuItem[] = [
    { key: '/', label: 'Dashboard', icon: DashboardOutlined },
    { key: '/billing', label: 'Billing', icon: BankOutlined },
    { key: '/finances', label: 'Finances', icon: DollarOutlined },
    ...(authStore.can('properties', 'view')
      ? [{ key: '/properties', label: 'Properties', icon: HomeOutlined }]
      : []),
    ...(authStore.can('bookings', 'view')
      ? [{ key: '/bookings', label: 'Bookings', icon: CalendarOutlined }]
      : []),
    ...(authStore.can('guests', 'view')
      ? [{ key: '/guests', label: 'Guests', icon: TeamOutlined }]
      : []),
    { key: '/requests', label: 'Feedback', icon: SolutionOutlined },
  ]

  return items
})

const addonItems = computed(() => {
  const items: Array<{ key: string; label: string; icon: any }> = []
  if (authStore.can('screens', 'view')) {
    items.push({ key: '/screens', label: 'TV Screens', icon: DesktopOutlined })
  }
  items.push(
    { key: '/calendar-sync', label: 'Calendar Sync', icon: CalendarOutlined },
    { key: '/landing-page', label: 'Landing Page', icon: GlobalOutlined },
    { key: '/fb-conversions', label: 'FB Conversions', icon: DollarOutlined },
    { key: '/fb-messenger-bot', label: 'FB Messenger Bot', icon: MessageOutlined },
    { key: '/inventory', label: 'Inventory', icon: InboxOutlined },
  )
  return items
})

const bottomMenuItems = computed(() => {
  const items: Array<{
    key: string
    label: string
    children: Array<{ key: string; label: string; icon: any }>
  }> = [
    {
      key: 'settings',
      label: 'Settings',
      children: [
        ...(authStore.can('companies', 'view')
          ? [{ key: '/settings/company', label: 'Company', icon: HomeOutlined }]
          : []),
        { key: '/settings/notifications', label: 'Notifications', icon: BellOutlined },
        ...(authStore.can('announcements', 'view')
          ? [
              {
                key: '/settings/announcements' as const,
                label: 'Announcements',
                icon: NotificationOutlined,
              },
            ]
          : []),
      ],
    },
  ]

  if (authStore.isAdmin) {
    items.push({
      key: 'admin',
      label: 'Admin',
      children: [
        { key: '/admin/users', label: 'Users', icon: UserSwitchOutlined },
        { key: '/admin/companies', label: 'Companies', icon: HomeOutlined },
        { key: '/admin/requests', label: 'Requests', icon: SolutionOutlined },
        { key: '/admin/contacts', label: 'Contacts', icon: TeamOutlined },
        { key: '/admin/announcements', label: 'Announcements', icon: NotificationOutlined },
        { key: '/admin/email-settings', label: 'Email', icon: MailOutlined },
        { key: '/admin/billing', label: 'Billing', icon: DollarOutlined },
        { key: '/admin/vouchers', label: 'Vouchers', icon: DollarOutlined },
      ],
    })
  } else if (authStore.can('companies', 'view')) {
    items.push({
      key: 'admin',
      label: 'Admin',
      children: [{ key: '/admin/companies', label: 'Companies', icon: HomeOutlined }],
    })
  }

  return items
})

const parentKeys = new Set(
  [...menuItems.value, ...bottomMenuItems.value].filter((i) => i.children).map((i) => i.key),
)

const selectedKeys = computed(() => [route.path])

const onMenuClick = ({ key }: { key: Key }) => {
  const path = String(key)

  if (path.startsWith('/')) {
    router.push(path).catch((err) => {
      if (err && err.name !== 'NavigationDuplicated') {
        console.error('Sidebar route guard push error:', err)
      }
    })
  }

  emit('menuClick', path)
}
</script>

<template>
  <a-layout-sider
    :collapsed="collapsed"
    :trigger="null"
    collapsible
    :theme="menuTheme"
    style="display: flex; flex-direction: column; height: 100vh; overflow: hidden"
  >
    <div class="logo">
      <img src="/favicon.ico" class="logo-icon" />
      <span v-if="!collapsed" class="logo-title">CasaMana</span>
    </div>
    <div class="menu-scroll">
      <a-menu
        :selectedKeys="selectedKeys"
        :openKeys="openKeys"
        :theme="menuTheme"
        mode="inline"
        @openChange="(keys: Key[]) => emit('update:openKeys', keys)"
        @click="onMenuClick"
      >
        <template v-for="item in menuItems" :key="item.key">
          <template v-if="item.children" :key="`${item.key}-group`">
            <a-sub-menu :key="item.key">
              <template #icon><component :is="item.icon" /></template>
              <template #title>{{ item.label }}</template>
              <a-menu-item v-for="child in item.children" :key="child.key">
                <template #icon><component :is="child.icon" /></template>
                {{ child.label }}
              </a-menu-item>
            </a-sub-menu>
          </template>
          <template v-else :key="`${item.key}-item`">
            <a-menu-item :key="item.key">
              <template #icon><component :is="item.icon" /></template>
              {{ item.label }}
            </a-menu-item>
          </template>
        </template>

        <a-menu-item-group title="Add-ons">
          <a-menu-item v-for="item in addonItems" :key="item.key">
            <template #icon><component :is="item.icon" /></template>
            {{ item.label }}
          </a-menu-item>
        </a-menu-item-group>

        <a-menu-divider />

        <a-menu-item-group v-for="item in bottomMenuItems" :key="item.key" :title="item.label">
          <a-menu-item v-for="child in item.children" :key="child.key">
            <template #icon><component :is="child.icon" /></template>
            {{ child.label }}
          </a-menu-item>
        </a-menu-item-group>
      </a-menu>
    </div>
  </a-layout-sider>
</template>

<style scoped>
.logo {
  height: 48px;
  margin: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  flex-shrink: 0;
}
.logo-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}
.logo-title {
  font-weight: bold;
  font-size: 16px;
  white-space: nowrap;
}
.menu-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

:deep(.ant-layout-sider-children) {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
</style>
